import axiosInstance from './axiosInstance';

const authService = {    // Customer login
    async customerLogin(credentials) {
        try {
            // Convert email to lowercase before sending
            const normalizedCredentials = {
                ...credentials,
                email: credentials.email.toLowerCase()
            };
            
            console.log('Sending login request with:', { email: normalizedCredentials.email });
            const response = await axiosInstance.post('/customers/signin', normalizedCredentials);
            console.log('Login response:', response.data);

            if (response.data.success) {
                // Store customer email for OTP verification (as lowercase)
                localStorage.setItem('customerEmail', normalizedCredentials.email);

                // Store token if provided
                if (response.data.token) {
                    localStorage.setItem('customerToken', response.data.token);
                    axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
                }
            }
            return response.data;
        } catch (error) {
            console.error('Login error:', error);
            console.error('Error response:', error.response?.data);
            throw error.response?.data || { success: false, message: error.message || 'Login failed' };
        }
    },    // Send Agent OTP
    async sendAgentOTP(email) {
        try {
            // Convert email to lowercase
            const normalizedEmail = email.toLowerCase();
            
            // Using the signin endpoint to trigger OTP sending
            const response = await axiosInstance.post('/agents/signin', { email: normalizedEmail });
            return response.data;
        } catch (error) {
            throw error.response?.data || { success: false, message: error.message || 'Failed to send OTP' };
        }
    },// Agent login
    async agentLogin(credentials) {
        try {
            // Convert email to lowercase
            const normalizedCredentials = {
                ...credentials,
                email: credentials.email.toLowerCase()
            };
            
            console.log('Agent login attempt with:', normalizedCredentials);
            const response = await axiosInstance.post('/agents/signin', normalizedCredentials);
            console.log('Login successful:', response.data);

            if (response.data.success) {
                // Store agent email for OTP verification (as lowercase)
                localStorage.setItem('agentEmail', normalizedCredentials.email);

                // Store token if provided
                if (response.data.token) {
                    localStorage.setItem('agentToken', response.data.token);
                    axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
                }

                return {
                    success: true,
                    message: 'OTP sent successfully'
                };
            }

            return response.data;
        } catch (error) {
            console.error('Agent login error:', error);
            if (!error.response) {
                return {
                    success: false,
                    message: 'Network error. Please check your internet connection and try again.'
                };
            }
            throw error.response?.data || { success: false, message: error.message || 'Login failed' };
        }
    },

    // Get customer details by ID
    async getCustomerDetails(customerId) {
        try {
            const response = await axiosInstance.post('/customers/details', { customerId });
            return response.data;
        } catch (error) {
            throw error.response?.data || { success: false, message: error.message || 'Failed to get customer details' };
        }
    },

    // Get customer businesses
    async getCustomerBusinesses(customerId) {
        try {
            const response = await axiosInstance.get(`/customers/businesses/${customerId}`);
            return response.data;
        } catch (error) {
            throw error.response?.data || { success: false, message: error.message || 'Failed to get customer businesses' };
        }
    },    // Verify OTP
    async verifyOTP(email, otp) {
        try {
            // Convert email to lowercase
            const normalizedEmail = email.toLowerCase();
            
            const response = await axiosInstance.post('/customers/verify-otp', { email: normalizedEmail, otp });
            if (response.data.success && response.data.token) {
                // Set the token in localStorage
                localStorage.setItem('customerToken', response.data.token);
                localStorage.setItem('customerOtpVerified', 'true');
                // Set the token in axios instance for future requests
                axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
            }
            return response.data;
        } catch (error) {
            throw error.response?.data || { success: false, message: error.message || 'OTP verification failed' };
        }
    },    // Verify Agent OTP
    async verifyAgentOTP(email, otp) {
        try {
            // Convert email to lowercase
            const normalizedEmail = email.toLowerCase();
            
            const response = await axiosInstance.post('/agents/verify-otp', { email: normalizedEmail, otp });
            if (response.data.success && response.data.token) {
                // Set the token in localStorage
                localStorage.setItem('agentToken', response.data.token);
                localStorage.setItem('agentOtpVerified', 'true');
                // Set the token in axios instance for future requests
                axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
            }
            return response.data;
        } catch (error) {
            throw error.response?.data || { success: false, message: error.message || 'OTP verification failed' };
        }
    },    // Resend OTP
    async resendOTP(email) {
        try {
            // Convert email to lowercase
            const normalizedEmail = email.toLowerCase();
            
            const response = await axiosInstance.post('/customers/resend-otp', { email: normalizedEmail });
            return response.data;
        } catch (error) {
            throw error.response?.data || { success: false, message: error.message || 'Failed to resend OTP' };
        }
    },    // Resend Agent OTP
    async resendAgentOTP(email) {
        try {
            // Convert email to lowercase
            const normalizedEmail = email.toLowerCase();
            
            const response = await axiosInstance.post('/agents/resend-otp', { email: normalizedEmail });
            return response.data;
        } catch (error) {
            throw error.response?.data || { success: false, message: error.message || 'Failed to resend OTP' };
        }
    },

    // Get customer profile
    async getProfile() {
        try {
            const response = await axiosInstance.get('/customers/profile');
            return response.data;
        } catch (error) {
            throw error.response?.data || { success: false, message: error.message || 'Failed to get profile' };
        }
    },

    // Update customer profile
    async updateProfile(profileData) {
        try {
            const response = await axiosInstance.put('/customers/profile', profileData);
            return response.data;
        } catch (error) {
            throw error.response?.data || { success: false, message: error.message || 'Failed to update profile' };
        }
    },

    // Logout
    logout() {
        // Remove token from axios instance
        delete axiosInstance.defaults.headers.common['Authorization'];
        // Remove token and verification from localStorage
        localStorage.removeItem('customerToken');
        localStorage.removeItem('customerEmail');
        localStorage.removeItem('customerOtpVerified');
    },

    // Agent logout
    agentLogout() {
        // Remove token from axios instance
        delete axiosInstance.defaults.headers.common['Authorization'];
        // Remove token and verification from localStorage
        localStorage.removeItem('agentToken');
        localStorage.removeItem('agentEmail');
        localStorage.removeItem('agentOtpVerified');
    },

    // Admin logout
    adminLogout() {
        // Remove token from axios instance
        delete axiosInstance.defaults.headers.common['Authorization'];
        // Remove token from localStorage
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminData');
    },

    // Check if user is authenticated
    isAuthenticated() {
        const token = localStorage.getItem('customerToken');
        return token && token !== 'null' && token !== 'undefined';
    },

    // Check if agent is authenticated
    isAgentAuthenticated() {
        const token = localStorage.getItem('agentToken');
        return token && token !== 'null' && token !== 'undefined';
    },

    // Check if admin is authenticated
    isAdminAuthenticated() {
        const token = localStorage.getItem('adminToken');
        return token && token !== 'null' && token !== 'undefined';
    },

    // Admin login
    async adminLogin(credentials) {
        try {
            console.log('Attempting admin login with:', { email: credentials.email });
            const response = await axiosInstance.post('/admin/login', credentials);
            console.log('Admin login response:', response.data);

            if (response.data.success) {
                // Store admin data and token
                localStorage.setItem('adminData', JSON.stringify(response.data.admin || {}));
                if (response.data.token) {
                    localStorage.setItem('adminToken', response.data.token);
                    // Set the token in axios instance for future requests
                    axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
                }
                return response.data;
            } else {
                throw new Error(response.data.message || 'Login failed');
            }
        } catch (error) {
            console.error('Admin login error:', error);
            if (!error.response) {
                throw {
                    success: false,
                    message: 'Network error. Please check your internet connection and try again.'
                };
            }
            throw error.response?.data || { success: false, message: error.message || 'Login failed' };
        }
    },

    // Initialize auth state
    initAuth() {
        const adminToken = localStorage.getItem('adminToken');
        const customerToken = localStorage.getItem('customerToken');
        const agentToken = localStorage.getItem('agentToken');

        if (adminToken && adminToken !== 'null' && adminToken !== 'undefined') {
            axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${adminToken}`;
        } else if (customerToken && customerToken !== 'null' && customerToken !== 'undefined') {
            axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${customerToken}`;
        } else if (agentToken && agentToken !== 'null' && agentToken !== 'undefined') {
            axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${agentToken}`;
        }
    },

    // Get agent details by ID
    async getAgentDetails(agentId) {
        try {
            const response = await axiosInstance.post('/agents/details', { agentId });
            return response.data;
        } catch (error) {
            throw error.response?.data || { success: false, message: error.message || 'Failed to get agent details' };
        }
    },    // Get agent details by email
    async getAgentByEmail(email) {
        try {
            // Convert email to lowercase
            const normalizedEmail = email.toLowerCase();
            
            const response = await axiosInstance.post('/agents/details-by-email', { email: normalizedEmail });
            return response.data;
        } catch (error) {
            if (!error.response) {
                return {
                    success: false,
                    agent: {
                        firstName: 'Agent',
                        lastName: 'User'
                    },
                    message: 'Network error. Using default agent info.'
                };
            }
            throw error.response?.data || { success: false, message: error.message || 'Failed to get agent details' };
        }
    },async requestPasswordResetOTP(email) {
        try {
            // Convert email to lowercase
            const normalizedEmail = email.toLowerCase();
            
            const response = await axiosInstance.post('/customers/request-password-reset', { email: normalizedEmail });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    async verifyResetOTP({ email, otp }) {
        try {
            // Convert email to lowercase
            const normalizedEmail = email.toLowerCase();
            
            const response = await axiosInstance.post('/customers/verify-reset-otp', { email: normalizedEmail, otp });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },    async checkEmailExists(email) {
        try {
            // Convert email to lowercase
            const normalizedEmail = email.toLowerCase();
            
            const response = await axiosInstance.post('/customers/check-email', { email: normalizedEmail });
            return response.data;
        } catch (error) {
            throw error.response?.data || { success: false, message: error.message || 'Failed to check email' };
        }
    },

    async resetPassword({ email, newPassword }) {
        try {
            // Convert email to lowercase
            const normalizedEmail = email.toLowerCase();
            
            const response = await axiosInstance.post('/customers/reset-password', { email: normalizedEmail, newPassword });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    // Activate customer account
    async activateCustomer(email) {
        try {
            // Convert email to lowercase
            const normalizedEmail = email.toLowerCase();
            
            console.log('Activating customer account:', { email: normalizedEmail });
            const response = await axiosInstance.post('/customers/activate', { email: normalizedEmail });
            console.log('Activation response:', response.data);
            return response.data;
        } catch (error) {
            console.error('Activation error:', error);
            console.error('Error response:', error.response?.data);
            throw error.response?.data || { success: false, message: error.message || 'Failed to activate account' };
        }
    },
};

// Initialize auth state when the service is imported
authService.initAuth();

export default authService; 